import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { IQuestionComponentRef, IQuestionModel } from '../../questions/types'
import { IAction, IActionComponentAPI, IActionComponentProps } from '../types'

import PreviewModule from '../../modules/preview/Module'
import TimelineContext from '../../modules/timeline/Timeline.Context'
import { getQuestionComponent, getQuestionModel } from '../../questions'

import useSession from '../../session/useSession'
import useViewport from '../../modules/viewport/useViewport'

import LoaderView from '../loader/View'
import InteractionsModule from '../../modules/interactions/Module'
import MouseLogModule from '../../modules/mouselog/Module'
import style from './style.module.scss'

interface IActionState {
  currentQuestionIndex: number
  currentQuestionModel: IQuestionModel
  questionModelsHistory: IQuestionModel[]
  baseScreenNumber: number
}
const actionState = new WeakMap<IAction, IActionState>()

const AskAction = React.memo(
  React.forwardRef<IActionComponentAPI, IActionComponentProps>(({ action }, ref) => {
    const lastsActionState: IActionState = actionState.get(action)

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(
      lastsActionState ? lastsActionState.currentQuestionIndex : 0
    )
    const [currentQuestionModel, setCurrentQuestionModel] = useState<IQuestionModel>(null)
    const questionModelsHistoryRef = useRef<IQuestionModel[]>(
      lastsActionState ? lastsActionState.questionModelsHistory : []
    )

    const currentQuestionComponentRef = useRef<IQuestionComponentRef>(null)
    const session = useSession()
    const viewport = useViewport()
    const writeInteractions = InteractionsModule.enabled
    if (writeInteractions) MouseLogModule.start()

    useEffect(() => {
      const lastsActionState: IActionState = actionState.get(action)

      const currentQuestionSchema = action?.data?.questions?.[currentQuestionIndex]
      const currentQuestionModel =
        lastsActionState &&
        lastsActionState.currentQuestionIndex === currentQuestionIndex &&
        lastsActionState.currentQuestionModel
          ? lastsActionState.currentQuestionModel
          : currentQuestionSchema
            ? getQuestionModel(currentQuestionSchema)(currentQuestionSchema, action)
            : null
      session.progress.updateFromAction(action, currentQuestionSchema?.id)
      session.currentTarget = currentQuestionModel?.id
      session.setWidgetWidth(null)
      session.enableNext(Boolean(currentQuestionModel?.responseValidation?.valid))
      if (currentQuestionModel) {
        session.enablePrev(action?.data?.back?.enable)
        session.showPrev(action?.data?.back?.available)
        session.enableNavigation(true)
      }
      if (action?.preview?.navigation_options) {
        PreviewModule.jumpToQuestionOptions = action.preview.navigation_options
      }
      if (action?.preview?.quotas) {
        PreviewModule.changeQuotaOptions = action.preview.quotas
        if (!session.currentQuota && PreviewModule.changeQuotaOptions.length) {
          session.currentQuota = PreviewModule.changeQuotaOptions[0].id
        }
      }
      if (action?.preview?.locales) {
        PreviewModule.localeOptions = action.preview.locales
        session.currentLocale = action?.data?.locale?.aytm_locale
      }
      if (action?.urls) session.urls = action.urls

      const baseScreenNumber =
        null == lastsActionState?.baseScreenNumber ? session.screenNumber : lastsActionState.baseScreenNumber
      session.screenNumber = getCurrentScreenNumberFromAction(
        baseScreenNumber,
        questionModelsHistoryRef.current,
        currentQuestionModel
      )

      actionState.set(action, {
        currentQuestionIndex: currentQuestionIndex,
        currentQuestionModel: currentQuestionModel,
        questionModelsHistory: questionModelsHistoryRef.current,
        baseScreenNumber
      })
      setCurrentQuestionModel(currentQuestionModel)
    }, [currentQuestionIndex, action, session])

    const QuestionComponent = useMemo(
      () =>
        currentQuestionModel ? getQuestionComponent(currentQuestionModel.type, currentQuestionModel.schema) : null,
      [currentQuestionModel]
    )

    useImperativeHandle<IActionComponentAPI, IActionComponentAPI>(ref, () => {
      const recalculateCurrentQuestionScreenNumber = () => {
        const currentActionState = actionState.get(action)
        if (currentActionState) {
          const screenNumber = getCurrentScreenNumberFromAction(
            currentActionState.baseScreenNumber,
            questionModelsHistoryRef.current,
            currentQuestionModel
          )
          if (session.screenNumber !== screenNumber) {
            session.screenNumber = screenNumber
            // autoscroll on screenNumber change
            session.scrollIntoView()
          }
        }
      }
      return {
        tryNext() {
          if (currentQuestionComponentRef.current) {
            if (currentQuestionComponentRef.current.tryNext()) {
              recalculateCurrentQuestionScreenNumber()
              return true
            } else {
              // commit current question frame time
              currentQuestionModel?.timeline.frameCommit(currentQuestionModel.frame)
              // switch to next question if exists
              if (currentQuestionIndex + 1 < action?.data?.questions?.length) {
                questionModelsHistoryRef.current.push(currentQuestionModel)
                setCurrentQuestionIndex(currentQuestionIndex + 1)
                return true
              } else {
                if (!session.screenNumberDisabled) {
                  // push current action base screen number to stack (to be used by prev action)
                  const currentActionState = actionState.get(action)
                  if (currentActionState) {
                    session.baseScreenNumbersStack.push(currentActionState.baseScreenNumber)
                  }
                }
                return false
              }
            }
          } else {
            return false
          }
        },
        tryPrev() {
          if (currentQuestionComponentRef.current) {
            if (currentQuestionComponentRef.current.tryPrev()) {
              recalculateCurrentQuestionScreenNumber()
              return true
            } else {
              if (0 < currentQuestionIndex) {
                // commit current question frame time
                currentQuestionModel?.timeline.frameCommit(currentQuestionModel.frame)
                // switch to prev question
                questionModelsHistoryRef.current.pop()
                setCurrentQuestionIndex(currentQuestionIndex - 1)
                return true
              } else {
                return false
              }
            }
          } else {
            return false
          }
        },
        showValidation() {
          if (currentQuestionComponentRef.current?.showValidation) {
            currentQuestionComponentRef.current.showValidation()
          }
        },
        get submitResponse() {
          const submitResponse = questionModelsHistoryRef.current.map(questionModel => {
            return {
              id: questionModel.id,
              response: questionModel.submitResponse,
              check_response: questionModel.checkResponse,
              log: questionModel.timeline.getLog(),
              durations: questionModel.timeline.getDurations(),
              ...(writeInteractions ? { interactions: MouseLogModule.getData() } : {})
            }
          })
          if (currentQuestionModel) {
            submitResponse.push({
              id: currentQuestionModel.id,
              response: currentQuestionModel.submitResponse,
              check_response: currentQuestionModel.checkResponse,
              log: currentQuestionModel.timeline.getLog(),
              durations: currentQuestionModel.timeline.getDurations(),
              ...(writeInteractions ? { interactions: MouseLogModule.getData() } : {})
            })
          }
          return submitResponse
        },
        get currentQuestionModel() {
          return currentQuestionModel
        }
      }
    }, [action, currentQuestionIndex, currentQuestionModel, session, writeInteractions])

    useEffect(() => {
      // autoscroll on question change
      session.scrollIntoView()
    }, [currentQuestionModel?.id, session])

    return (
      <TimelineContext.Provider value={currentQuestionModel?.timeline}>
        <div className={`${style.AskAction} ${style.action} ${viewport.mobile ? style.mobile : ''}`}>
          <div className={style.view}>
            <React.Suspense fallback={<LoaderView />}>
              {QuestionComponent ? (
                <QuestionComponent
                  model={currentQuestionModel}
                  ref={currentQuestionComponentRef}
                  key={currentQuestionModel?.id}
                />
              ) : (
                <LoaderView />
              )}
            </React.Suspense>
          </div>
        </div>
      </TimelineContext.Provider>
    )
  }),
  (prevProps, nextProps) => prevProps.action === nextProps.action
)

AskAction.displayName = 'AskAction'

export default AskAction

function getCurrentScreenNumberFromAction(
  baseScreenNumber: number,
  questionModelsHistory: IQuestionModel[],
  currentQuestionModel: IQuestionModel
) {
  return (
    baseScreenNumber +
    questionModelsHistory.reduce((sum, questionModel) => sum + questionModel.screenNumber, 0) +
    currentQuestionModel.screenNumber
  )
}
